import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Button, Table, Message, Icon, Input, Label, Segment, Header, Dropdown, Checkbox, Form, Modal, Tab, Loader, Radio,
} from 'semantic-ui-react';
import apiClient from '../../apiTools/apiClient';
import ConfirmModal from '../../components/prototype/ConfirmModal';
import { Spinner } from '@react-pdf-viewer/core';
/**
 * ClientReleasesManager — the admin side of the client download page (/listen).
 *
 * A RELEASE is a set of files a client may download: one MP3 per chosen chapter, or the chosen chapters
 * joined into ONE MP3 for the whole book. Each chapter is a specific TAKE (a stitched or mastered
 * :RenderedChapter), copied into the release when it is built — so re-stitching later never changes
 * what a client already has. A KEY is one client's access to one or more releases.
 *
 * ⚠️ Only a unit Stitch (Stitch panel) or a master produces a take. "Stitch to one MP3" on the Render
 * Studio table saves nothing and does not appear here.
 *
 * See docs/journal/CLIENT_LISTEN_PAGE_DESIGN.md.
 */

const fmtDuration = (ms) => {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};
const fmtSize = (b) => (b ? `${(b / 1048576).toFixed(1)} MB` : '—');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/**
 * ─── ⭐⭐ SEMANTIC FORM LAYOUT, SOLVED ONCE ──────────────────────────────────────────────────────
 *
 * ⛔ THE RECURRING BUG. A Semantic `Radio`/`Checkbox` renders its control box with
 * `position: absolute` and its text in a `<label>` that is NOT counted in the element's width. In an
 * `inline` `Form.Group` — or anywhere the element is laid out as a block-level field — the next
 * control starts before the previous label has finished, and the text draws over the button.
 *
 * ⭐ THE FIX IS `display: inline-table`. A table box SHRINK-WRAPS to its content, so the element
 * finally measures its own label and nothing after it can overlap. The operator found this after
 * measuring the shortfall in DevTools at ~80–100 px of `margin-left`.
 *
 * ⛔ WHAT DOES NOT WORK, so it is not tried again:
 *   • flex `gap` on the container — gap spaces ITEMS, and the overflow is INSIDE one;
 *   • a hard `margin-left` on the later control — it is the size of the current label, so it breaks
 *     silently the next time the wording changes;
 *   • `Form.Field` wrapping — it reintroduces the block-level field whose width is the problem.
 *
 * ⚠️ `marginRight` on EVERY radio rather than `marginLeft` on the second: separation then composes if
 * a third option is ever added, instead of being positional.
 */
const RADIO = Object.freeze({ display: 'inline-table', marginRight: '2rem', marginBottom: '.5rem' });

/** A standalone field label, used in place of `Form.Field`'s own. */
const FIELD_LABEL = Object.freeze({ display: 'block', marginBottom: '.5rem' });

/** ⚠️ Width is explicit: a Semantic `Input` in a `Form` with no field wrapper stretches full width. */
const TEXT_INPUT = Object.freeze({ display: 'block', marginBottom: '.5rem', width: '300px' });

/**
 * One status pill that cannot grow. See the note at the Status cell for what it fixes.
 * `inline-flex` + `nowrap` keep the icon and the word on one line; `margin: 0` overrides the global
 * `.ui.segment .ui.label` rule that `Tab.Pane` drags in.
 */
/**
 * ⭐ A STATUS PILL AS A PLAIN SPAN, not a Semantic Label — the operator's own call, and it removes
 * every workaround this cell accumulated.
 *
 * ⛔ WHAT THE LABEL COST. Semantic renders a checkbox-style control box with position:absolute and
 * keeps the text in a label that is not counted in the width, so the pill grew and had to be pinned
 * to an explicit 30px height; and a global rule in styles/userflow.css,
 * `.ui.segment .ui.label { margin: 5px }`, reached it because Tab.Pane renders as .ui.segment, so
 * margin: 0 was load-bearing too. A span owns its own box and needs none of that.
 *
 * ⚠️ The values match Semantic's own small label (font .78571429rem, weight 700, radius .28571429rem,
 * and its green/blue/red) so the table looks unchanged.
 */
const pill = (background) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '.45em',
  whiteSpace: 'nowrap',
  background,
  color: '#fff',
  borderRadius: '.28571429rem',
  padding: '.4em .75em',
  fontSize: '.78571429rem',
  fontWeight: 700,
  lineHeight: 1,
});
const PILL_GREEN = '#21ba45';
const PILL_BLUE = '#2185d0';
const PILL_RED = '#db2828';
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—');
const errText = (err) => err?.response?.data?.error || err.message;
const stageLabel = (stage) => (stage === 'mastered' ? 'Mastered' : 'Stitched');

/** The take a chapter defaults to: the active master, else the active stitch, else the newest. */
function defaultTake(takes) {
  return takes.find((t) => t.isActive && t.stage === 'mastered')
    || takes.find((t) => t.isActive)
    || takes[0];
}

// ─── Releases ─────────────────────────────────────────────────────────────────────────────────

function ReleasesPane({ releases, reload, setError, setNotice }) {
  const [books, setBooks] = useState([]);
  const [bookId, setBookId] = useState(null);
  const [takes, setTakes] = useState([]);
  const [loadingTakes, setLoadingTakes] = useState(false);
  const [chosen, setChosen] = useState({});        // chapterNumber → take uuid (absent = not included)
  /**
   * ⭐ ONE FILE IS THE DEFAULT, not one per chapter.
   *
   * A whole-book release is what an audiobook listener actually wants, and per-chapter means 44
   * downloads for Mask. The old default cost a wasted build: the operator rebuilt Mask after a
   * correction, got 44 files instead of the single file the original release had, and had to build a
   * third release to get back to one. The destructive-ish choice should not be the quiet one.
   */
  const [kind, setKind] = useState('book');
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    apiClient.get('/listen/admin/books')
      .then(({ data }) => setBooks(data.books || []))
      .catch((err) => setError(errText(err)));
  }, [setError]);

  const book = books.find((b) => b.bookId === bookId) || null;

  useEffect(() => {
    if (!bookId) { setTakes([]); setChosen({}); return; }
    setLoadingTakes(true);
    apiClient.get(`/listen/admin/books/${bookId}/takes`)
      .then(({ data }) => { setTakes(data.takes || []); setChosen({}); })
      .catch((err) => setError(errText(err)))
      .finally(() => setLoadingTakes(false));
  }, [bookId, setError]);

  useEffect(() => { if (book) setTitle(book.title || ''); }, [book]);

  // chapterNumber → its takes, newest first within each stage (the server's order)
  const chapters = useMemo(() => {
    const m = new Map();
    for (const t of takes) {
      if (!m.has(t.chapterNumber)) m.set(t.chapterNumber, []);
      m.get(t.chapterNumber).push(t);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [takes]);

  const takeByUuid = useMemo(() => new Map(takes.map((t) => [t.uuid, t])), [takes]);
  const picked = Object.values(chosen).map((u) => takeByUuid.get(u)).filter(Boolean);
  const pickedMs = picked.reduce((a, t) => a + (t.durationMs || 0), 0);
  const pickedBytes = picked.reduce((a, t) => a + (t.audioBytes || 0), 0);

  const toggle = (num, list) => setChosen((prev) => {
    const next = { ...prev };
    if (next[num]) delete next[num]; else next[num] = defaultTake(list).uuid;
    return next;
  });
  const selectAll = () => setChosen(Object.fromEntries(chapters.map(([n, list]) => [n, defaultTake(list).uuid])));

  const create = async () => {
    setCreating(true); setError(null); setNotice(null);
    try {
      await apiClient.post('/listen/admin/releases', {
        bookId, title: title.trim() || (book && book.title), kind, chapterUuids: Object.values(chosen),
      });
      setNotice(`"${title.trim() || book.title}" is building. It appears below and turns Ready when its files are in place.`);
      setChosen({});
      await reload();
    } catch (err) {
      setError(errText(err));
    } finally { setCreating(false); }
  };

  const doDelete = async () => {
    setDeleting(true); setError(null);
    try {
      await apiClient.delete(`/listen/admin/releases/${toDelete.uuid}`);
      setNotice(`Release "${toDelete.title}" deleted.`);
      setToDelete(null);
      await reload();
    } catch (err) {
      setError(errText(err));
    } finally { setDeleting(false); }
  };

  const bookOptions = books.map((b) => ({
    key: b.bookId, value: b.bookId,
    text: `${b.title} — ${b.litGroup || 'no LitGroup'}`,
    description: `${b.chapters} ch · ${b.takes} takes`,
  }));

  return (
    <>
      <Segment>
        <Header as="h4">New release</Header>
        <Form>
          <Form.Field>
            <label>Book</label>
            <Dropdown
              selection search fluid placeholder={books.length ? 'Pick a book' : 'No book has a stitched or mastered chapter yet'}
              options={bookOptions} value={bookId} onChange={(_, d) => setBookId(d.value)}
            />
          </Form.Field>
        </Form>

        {loadingTakes && <Loader active inline="centered" style={{ marginTop: 16 }} />}

        {bookId && !loadingTakes && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '14px 0 6px' }}>
              <Button size="mini" onClick={selectAll}>Select all</Button>
              <Button size="mini" onClick={() => setChosen({})} disabled={!picked.length}>None</Button>
              <span style={{ color: '#666' }}>
                {picked.length} of {chapters.length} chapters · {fmtDuration(pickedMs)} · {fmtSize(pickedBytes)}
              </span>
            </div>

            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <Table compact="very" celled unstackable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell collapsing />
                    <Table.HeaderCell collapsing>Ch</Table.HeaderCell>
                    <Table.HeaderCell>Take</Table.HeaderCell>
                    <Table.HeaderCell collapsing>Length</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {chapters.map(([num, list]) => {
                    const sel = chosen[num];
                    const cur = takeByUuid.get(sel) || defaultTake(list);
                    return (
                      <Table.Row key={num} active={!!sel}>
                        <Table.Cell collapsing textAlign="center">
                          <Checkbox checked={!!sel} onChange={() => toggle(num, list)} />
                        </Table.Cell>
                        <Table.Cell collapsing>{num}</Table.Cell>
                        <Table.Cell>
                          <Dropdown
                            inline disabled={!sel}
                            value={cur.uuid}
                            onChange={(_, d) => setChosen((p) => ({ ...p, [num]: d.value }))}
                            options={list.map((t) => ({
                              key: t.uuid, value: t.uuid,
                              text: `${stageLabel(t.stage)} · ${fmtDate(t.createdAt)}${t.isActive ? '' : ' · superseded'}`,
                            }))}
                          />
                          {list.length > 1 && <span style={{ color: '#999', marginLeft: 6 }}>({list.length} takes)</span>}
                        </Table.Cell>
                        <Table.Cell collapsing>{fmtDuration(cur.durationMs)}</Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>

            <Form style={{ marginTop: 14 }}>
              {/**
                * ⭐ Styles come from `RADIO` / `FIELD_LABEL` / `TEXT_INPUT` at the top of this file,
                * where the Semantic overlap problem and the `inline-table` fix are explained once.
                *
                * ⛔ NO `Form.Field` HERE, deliberately: its block-level field is the width that
                * caused the overlap, and wrapping these would reintroduce it.
                *
                * ⭐ ONE file is listed first because it is the default — see `kind`'s initial state.
                */}
              {/* ⭐ `name` makes these a real radio group: arrow keys move between them, and a
                  screen reader announces "1 of 2". The controlled `checked` still decides state. */}
              <label style={FIELD_LABEL}>Deliver as</label>
              <Radio
                name="deliverAs"
                value="book"
                label="The whole selection as ONE file"
                checked={kind === 'book'}
                onChange={() => setKind('book')}
                style={RADIO}
              />
              <Radio
                name="deliverAs"
                value="chapters"
                label="One file per chapter"
                checked={kind === 'chapters'}
                onChange={() => setKind('chapters')}
                style={RADIO}
              />
              <label style={{ ...FIELD_LABEL, marginTop: '.5rem' }}>Release title (what the client sees)</label>
              <Input style={TEXT_INPUT} value={title} onChange={(e) => setTitle(e.target.value)} />
              <Button primary disabled={!picked.length || creating} loading={creating} onClick={create}>
                Create release ({picked.length} {picked.length === 1 ? 'chapter' : 'chapters'})
              </Button>
            </Form>
          </>
        )}
      </Segment>

      <Header as="h4">Releases</Header>
      {releases.length === 0 ? <p style={{ color: '#666' }}>None yet.</p> : (
        <Table compact celled unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Title</Table.HeaderCell>
              <Table.HeaderCell>Book</Table.HeaderCell>
              <Table.HeaderCell>Files</Table.HeaderCell>
              <Table.HeaderCell collapsing>Status</Table.HeaderCell>
              <Table.HeaderCell collapsing>Keys</Table.HeaderCell>
              <Table.HeaderCell collapsing>Created</Table.HeaderCell>
              <Table.HeaderCell collapsing />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {releases.map((r) => {
              const ms = r.items.reduce((a, i) => a + (i.durationMs || 0), 0);
              const bytes = r.items.reduce((a, i) => a + (i.audioBytes || 0), 0);
              const chaptersIn = r.items.reduce((a, i) => a + ((i.sourceChapterUuids || []).length || 1), 0);
              return (
                <Table.Row key={r.uuid}>
                  <Table.Cell>{r.title}</Table.Cell>
                  <Table.Cell>{r.bookTitle || '—'}</Table.Cell>
                  <Table.Cell>
                    {r.status === 'ready'
                      ? <>{r.kind === 'book' ? `1 file (${chaptersIn} ch)` : `${r.items.length} files`} · {fmtDuration(ms)} · {fmtSize(bytes)}</>
                      : (r.kind === 'book' ? 'one file' : 'one per chapter')}
                  </Table.Cell>
                  {/**
                    * ⛔ SPANS, NOT `Label` — and the history is worth keeping, because this cell took
                    * four attempts.
                    *
                    * "Building" rendered ~100px tall. The cell is `collapsing` (Semantic: `width: 1px`)
                    * and a Semantic Label does not count its own label text in its width, so the text
                    * wrapped toward one character per line. `inline-flex` + `nowrap` stopped the wrap but
                    * the pill still grew, so it needed an explicit height; and `margin: 0` was
                    * load-bearing too, because a GLOBAL rule in `styles/userflow.css`,
                    * `.ui.segment .ui.label { margin: 5px }`, reaches it via `Tab.Pane` rendering as
                    * `.ui.segment`.
                    *
                    * ⭐ Three workarounds for one component. A span owns its own box and needs none of
                    * them — see `pill()` at the top of this file. The operator called it: *"I think I
                    * should have used just a span."*
                    */}
                  <Table.Cell collapsing>
                    {r.status === 'ready' && <span style={pill(PILL_GREEN)}>Ready</span>}
                    {r.status === 'building' && (
                      <span style={pill(PILL_BLUE)}>
                        <Spinner size="1.3rem" />
                        Building
                      </span>
                    )}
                    {r.status === 'failed' && (
                      <span style={pill(PILL_RED)} title={r.error || ''}>Failed</span>
                    )}
                    {r.status === 'failed' && r.error && <div style={{ color: '#b32424', fontSize: '0.85em', maxWidth: 260 }}>{r.error}</div>}
                  </Table.Cell>
                  <Table.Cell collapsing textAlign="center">{r.keys}</Table.Cell>
                  <Table.Cell collapsing>{fmtDay(r.createdAt)}</Table.Cell>
                  <Table.Cell collapsing>
                    <Button size="mini" basic color="red" icon="trash" disabled={r.status === 'building'} onClick={() => setToDelete(r)} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}

      <ConfirmModal
        open={!!toDelete}
        title="Delete this release?"
        items={toDelete ? [toDelete.title] : []}
        note={toDelete && toDelete.keys
          ? `${toDelete.keys} key(s) grant it; those clients will no longer see it. Their other releases are untouched.`
          : 'Its files are removed. The takes it was made from are not.'}
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => setToDelete(null)}
      />
    </>
  );
}

// ─── Keys ─────────────────────────────────────────────────────────────────────────────────────

function keyState(k) {
  if (k.revokedAt) return { text: 'Revoked', color: 'grey' };
  if (k.expiresAt && new Date(k.expiresAt) < new Date()) return { text: 'Expired', color: 'orange' };
  return { text: 'Active', color: 'green' };
}

function KeysPane({ releases, keys, reload, setError, setNotice }) {
  const [form, setForm] = useState({ label: '', expiresInDays: 90, releaseUuids: [] });
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState(null);        // { key, label, expiresAt } — shown ONCE
  const [copied, setCopied] = useState(false);
  const [toRevoke, setToRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);

  /**
   * ⭐ WHY THESE OPTIONS CARRY SO MUCH. A release is immutable once built — you cannot rebuild one in
   * place — so correcting a book means building a SECOND release with the same title. That is now the
   * normal case, not an edge case: the operator hit it the first time they fixed a line, and
   * *"I now have 2 The Mask of Madonna 13 entries. I don't know which is the latest."*
   *
   * Granting the wrong one is silent and expensive: a key's grants cannot be edited after issue, so
   * the mistake is only fixable by revoking the key and issuing another.
   *
   * ⚠️ `fmtDate`, not `fmtDay` — a corrected release is usually built the SAME DAY as the one it
   * replaces, so a date alone disambiguates nothing. The time is the whole point.
   */
  const readyReleases = releases.filter((r) => r.status === 'ready');
  // Newest first: after a correction the one you want is almost always the one you just built.
  const sortedReady = [...readyReleases].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
  // ⭐ Only mark "latest" where a title is actually duplicated — a lone release needs no decoration.
  const titleCounts = sortedReady.reduce((acc, r) => {
    acc[r.title] = (acc[r.title] || 0) + 1;
    return acc;
  }, {});
  const seenTitles = {};
  const readyOptions = sortedReady.map((r) => {
    const items = r.items || [];
    const ms = items.reduce((a, i) => a + (i.durationMs || 0), 0);
    const bytes = items.reduce((a, i) => a + (i.audioBytes || 0), 0);
    // ⚠️ Files, not chapters: a 'book' release is ONE file joined from many chapters, and the
    // listener's download count is what actually differs between the two shapes.
    const files = r.kind === 'book'
      ? `1 file (${items.reduce((a, i) => a + ((i.sourceChapterUuids || []).length || 1), 0)} ch)`
      : `${items.length} files`;
    const dup = titleCounts[r.title] > 1;
    seenTitles[r.title] = (seenTitles[r.title] || 0) + 1;
    const isNewestOfTitle = dup && seenTitles[r.title] === 1;
    return {
      key: r.uuid,
      value: r.uuid,
      // The chip shown AFTER selection only renders `text`, so the marker has to live here to
      // survive picking — a description would vanish exactly when it is needed for review.
      text: isNewestOfTitle ? `${r.title} — latest` : r.title,
      description: [
        fmtDate(r.createdAt),
        files,
        fmtDuration(ms),
        fmtSize(bytes),
        r.bookTitle && r.bookTitle !== r.title ? r.bookTitle : null,
      ].filter(Boolean).join(' · '),
    };
  });

  const issue = async () => {
    setIssuing(true); setError(null); setNotice(null);
    try {
      const { data } = await apiClient.post('/listen/admin/keys', {
        label: form.label.trim(), expiresInDays: Number(form.expiresInDays) || 90, releaseUuids: form.releaseUuids,
      });
      setIssued({ key: data.key, label: form.label.trim(), expiresAt: data.expiresAt });
      setCopied(false);
      setForm({ label: '', expiresInDays: 90, releaseUuids: [] });
      await reload();
    } catch (err) {
      setError(errText(err));
    } finally { setIssuing(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(issued.key); setCopied(true); } catch (_) { setCopied(false); }
  };

  const doRevoke = async () => {
    setRevoking(true); setError(null);
    try {
      await apiClient.post(`/listen/admin/keys/${toRevoke.uuid}/revoke`);
      setNotice(`Key for "${toRevoke.label}" revoked. It stops working immediately, even on a page already open.`);
      setToRevoke(null);
      await reload();
    } catch (err) {
      setError(errText(err));
    } finally { setRevoking(false); }
  };

  return (
    <>
      <Segment>
        <Header as="h4">Issue a key</Header>
        <Form>
          <Form.Group widths="equal">
            <Form.Field required>
              <label>For whom</label>
              <Input placeholder="e.g. Aunt Sue" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
              {/* The client has no account, so this label is the only name /listen can greet them by. */}
              <div style={{ color: '#666', fontSize: '0.85em', marginTop: 4 }}>
                Shown to the client in the page header, so write it as their name.
              </div>
            </Form.Field>
            <Form.Field width={4}>
              <label>Expires in (days)</label>
              <Input type="number" min={1} max={3650} value={form.expiresInDays}
                onChange={(e) => setForm((f) => ({ ...f, expiresInDays: e.target.value }))} />
            </Form.Field>
          </Form.Group>
          <Form.Field required>
            <label>Releases this key can download</label>
            <Dropdown
              multiple selection search
              style={{ minWidth: 250, maxWidth: '100%' }}
              placeholder={readyOptions.length ? 'Pick one or more' : 'No release is ready yet'}
              options={readyOptions} value={form.releaseUuids}
              onChange={(_, d) => setForm((f) => ({ ...f, releaseUuids: d.value }))}
            />
          </Form.Field>
          <Button primary loading={issuing}
            disabled={issuing || !form.label.trim() || !form.releaseUuids.length} onClick={issue}>
            Issue key
          </Button>
        </Form>
      </Segment>

      <Header as="h4">Keys</Header>
      {keys.length === 0 ? <p style={{ color: '#666' }}>None yet.</p> : (
        <Table compact celled unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>For</Table.HeaderCell>
              <Table.HeaderCell>Releases</Table.HeaderCell>
              <Table.HeaderCell collapsing>Status</Table.HeaderCell>
              <Table.HeaderCell collapsing>Expires</Table.HeaderCell>
              {/* ⭐ "Redeemed", not "Last used". `lastUsedAt` was written by touchKey on every
                  anonymous session open; stage 3b removed that path and its only writer, so this
                  column would have read "never" forever for keys that had plainly been claimed.
                  Redemption is also the better question: not how often a credential was presented,
                  but whether the person it was sent to ever set up their account. */}
              <Table.HeaderCell collapsing>Redeemed</Table.HeaderCell>
              <Table.HeaderCell collapsing />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {keys.map((k) => {
              const st = keyState(k);
              return (
                <Table.Row key={k.uuid} disabled={st.text !== 'Active'}>
                  <Table.Cell>{k.label}</Table.Cell>
                  <Table.Cell>{k.releases.map((r) => r.title).join(', ') || '—'}</Table.Cell>
                  <Table.Cell collapsing><Label size="small" color={st.color}>{st.text}</Label></Table.Cell>
                  <Table.Cell collapsing>{fmtDay(k.expiresAt)}</Table.Cell>
                  {/* ⓘ Falls back to the pre-3b `lastUsedAt` so keys issued before the change keep
                      the history they do have, rather than all reading "not yet". */}
                  <Table.Cell collapsing>
                    {k.redeemedAt
                      ? fmtDate(k.redeemedAt)
                      : (k.lastUsedAt ? `${fmtDate(k.lastUsedAt)} (${k.useCount})` : 'not yet')}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {!k.revokedAt && <Button size="mini" basic color="red" onClick={() => setToRevoke(k)}>Revoke</Button>}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}

      {/* ⚠️ The only time the key is ever visible. It is stored hashed; nobody can show it again. */}
      <Modal open={!!issued} size="tiny" closeOnDimmerClick={false} onClose={() => setIssued(null)}>
        <Modal.Header><Icon name="key" /> Key for {issued && issued.label}</Modal.Header>
        <Modal.Content>
          <div style={{
            fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '1.35em', letterSpacing: '0.04em',
            padding: '14px 12px', background: '#f6f7f9', border: '1px solid #dde', borderRadius: 4,
            textAlign: 'center', userSelect: 'all', wordBreak: 'break-all',
          }}>
            {issued && issued.key}
          </div>
          <Message warning size="small">
            <strong>This is the only time the key is shown.</strong> Copy it now and send it to the client.
            If it is lost, revoke it and issue a new one.
          </Message>
          <p style={{ color: '#666' }}>
            The client opens <strong>{window.location.origin}/listen</strong> and types or pastes the key.
            It expires {issued && fmtDay(issued.expiresAt)}.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={copy} color={copied ? 'green' : undefined}>
            <Icon name={copied ? 'check' : 'copy'} /> {copied ? 'Copied' : 'Copy key'}
          </Button>
          <Button primary onClick={() => setIssued(null)}>Done</Button>
        </Modal.Actions>
      </Modal>

      <ConfirmModal
        open={!!toRevoke}
        title="Revoke this key?"
        items={toRevoke ? [toRevoke.label] : []}
        note="It stops working at once, including on a page the client already has open. This cannot be undone; issue a new key to restore access."
        confirmLabel="Revoke"
        loading={revoking}
        onConfirm={doRevoke}
        onCancel={() => setToRevoke(null)}
      />
    </>
  );
}

// ─── the page ─────────────────────────────────────────────────────────────────────────────────

const ClientReleasesManager = () => {
  const [releases, setReleases] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const pollRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const [r, k] = await Promise.all([apiClient.get('/listen/admin/releases'), apiClient.get('/listen/admin/keys')]);
      setReleases(r.data.releases || []);
      setKeys(k.data.keys || []);
    } catch (err) {
      setError(errText(err));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // While anything is building, look again every few seconds. A whole book is a download, a join and
  // an upload of hundreds of megabytes, so "building" can last minutes.
  const building = releases.some((r) => r.status === 'building');
  useEffect(() => {
    if (!building) return undefined;
    pollRef.current = setInterval(reload, 4000);
    return () => clearInterval(pollRef.current);
  }, [building, reload]);

  const panes = [
    {
      menuItem: `Releases (${releases.length})`, render: () => (
        <Tab.Pane><ReleasesPane releases={releases} reload={reload} setError={setError} setNotice={setNotice} /></Tab.Pane>
      )
    },
    {
      menuItem: `Access keys (${keys.filter((k) => keyState(k).text === 'Active').length} active)`, render: () => (
        <Tab.Pane><KeysPane releases={releases} keys={keys} reload={reload} setError={setError} setNotice={setNotice} /></Tab.Pane>
      )
    },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px' }}>
      <Header as="h2">
        <Icon name="download" />
        <Header.Content>
          Client downloads
          <Header.Subheader>What clients can download at <code>/listen</code>, and who holds a key.</Header.Subheader>
        </Header.Content>
      </Header>
      {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}
      {notice && <Message positive onDismiss={() => setNotice(null)}>{notice}</Message>}
      {loading ? <Loader active inline="centered" /> : <Tab panes={panes} />}
    </div>
  );
};

export default ClientReleasesManager;
